import { useCallback, useState } from "react";
import type { FocusEvent, KeyboardEvent } from "react";

/**
 * Expansion of the icon rail: it opens while the pointer is over it or focus is inside it, and
 * stays open when pinned with its toggle. Escape closes a pin or a focus-open rail.
 */
export const useContextNavigationRailInternalState = (isExpandable: boolean) => {
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const onFocus = useCallback(() => setHasFocus(true), []);
  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHasFocus(false);
  }, []);
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") return;
    setIsPinned(false);
    setHasFocus(false);
  }, []);

  return {
    isExpanded: isExpandable && (isPinned || isHovered || hasFocus),
    isPinned,
    toggle: useCallback(() => setIsPinned((pinned) => !pinned), []),
    onMouseEnter: useCallback(() => setIsHovered(true), []),
    onMouseLeave: useCallback(() => setIsHovered(false), []),
    onFocus,
    onBlur,
    onKeyDown,
  };
};
